import './_login.scss';
import React, { useEffect, useRef, useState } from 'react';
import { Grid, FormGroup, ControlLabel, FormControl, Button, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import qs from 'qs';

import Loader from '../../components/Loaders/Loader';

import { getAccessRoutes } from '../../routers/helpers';
import { delay, getErrorMessage, isUrlEditor, parseUrl } from '../../utils/helper';
import { authLogin } from '../../services/endpoints/auth';
import { getNavbarConfig, getNavbarCounters } from '../../services/endpoints/general';
import { getNavbarConfigSelector } from '../../redux/selectors/header';
import { userAuthSuccess } from '../../redux/actions/user';
import * as actionTypes from '../../redux/types/header';
import * as actionTypesGeneral from '../../redux/types/general';

const Login = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const formRef = useRef(null);
  const query = qs.parse(location.search.slice(1), {
    delimiter: ';',
  });

  const recaptcha = useRef(null);
  const navbar = useSelector((state) => getNavbarConfigSelector(state));
  const user = useSelector((state) => state.user);
  const { isAuth } = user;
  const { menu } = navbar;

  const [data, setData] = useState({
    init: false,
    isLoading: false,
    redirectText: '',
    error: user?.error || null,
    success: false,
    captcha: window.appConfig?.captcha_required || !!user?.error || null,
    captcha_key: window.appConfig?.captcha_site_key || '',
    captcha_type: window.appConfig?.captcha_type || '',
    captcha_reload: false,
    second_factor: false,
  });

  const { init, isLoading, redirectText, error, captcha, captcha_key, captcha_type, success, captcha_reload } = data;

  const onCaptchaError = (captchaError) => {
    setData((prevState) => ({
      ...prevState,
      error: captchaError,
    }));
  };

  const fetchNavbarConfig = async () => {
    try {
      const response = await Promise.all([getNavbarConfig(), getNavbarCounters()]);
      const [config, counters] = response;
      const menuWithCounters = config.data?.menu?.map((item) => {
        const finded = counters?.data?.find((c) => c.code === item.code);
        if (finded) {
          return {
            ...item,
            count: finded.count,
          };
        }
        return item;
      });
      dispatch({
        type: actionTypes.GET_NAVBAR_SUCCESS,
        data: config.data,
        menu: menuWithCounters,
      });
      dispatch({
        type: actionTypesGeneral.CHANGE_VIEW_MODE_SUCCESS,
        payload: config.data.dark_mode,
      });

      return config.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setData((prevState) => ({
        ...prevState,
        error: `Navbar: ${errorMessage}`,
      }));
      throw error;
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setData((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    const re_captcha = captcha_type === 'h-captcha' ? formRef.current?.['h-captcha-response']?.value : formRef.current?.['g-recaptcha-response']?.value;
    const payload = {
      login: formRef.current?.login?.value,
      password: formRef.current?.password?.value,
      re_captcha: re_captcha || '',
    };

    try {
      /**
       * 1 - Авторизация
       */
      const response = await authLogin(payload);
      if (response.data?.access_token) {
        const { access_token, captcha, second_factor } = response.data;
        localStorage.setItem('access_token', access_token);
        setData((prevState) => ({
          ...prevState,
          captcha,
          second_factor: second_factor.required,
          success: true,
        }));
        if (second_factor?.required) {
          window.appConfig.second_factor = second_factor?.mode === 'app' ? second_factor : second_factor.required;
          window.appConfig.captcha = captcha;
          if (second_factor?.mode === 'app') {
            window.appConfig.websocket_url = response.data?.websocket_url;
            window.appConfig.reserve_websocket_url = response.data?.reserve_websocket_url;
            localStorage.setItem('2fa_code_time', Date.now());
            localStorage.setItem('2fa_code', window.appConfig?.second_factor?.code);
          }

          await delay(500);
          const pathname = second_factor?.mode === 'app' ? '/2fa/app' : '/2fa';
          history.replace({
            pathname,
            search: `${history.location.search}`,
          });
          return {};
        }
      }

      /**
       * 2 - Запрос Navbar
       * Для списка приватных роутов
       */
      const navbar = await fetchNavbarConfig();
      const firstRoute = navbar?.menu[0];

      /**
       * 3 - Убираем каптчу
       */
      window.appConfig.captcha_required = false;

      /**
       * 4 - Редирект на приватную страницу
       */
      await delay(500);
      await dispatch(userAuthSuccess(response.data));

      const allAccessRoutes = getAccessRoutes();
      const queryRedirect = query?.redirect ? query.redirect.replace(/^(admin)|(\/admin)/gm, '') : null;
      const url = parseUrl(queryRedirect);
      const firstUrl = firstRoute?.url || '/users';
      const findRoute = allAccessRoutes?.find((r) => {
        if (url) {
          return r.link === url.pathname;
        }
        return false;
      });
      const redirect = query.redirect === '/' || query.redirect === '/2fa' ? firstUrl : findRoute?.link || firstUrl;
      const isEditor = isUrlEditor(query.redirect);
      isEditor
        ? (window.location.href = '/admin/editor')
        : history.push({
          pathname: redirect,
          search: url?.query,
          hash: url?.fragment ? `#${url.fragment}` : null,
        });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setData((prevState) => ({
        ...prevState,
        error: errorMessage,
        captcha: true,
        isLoading: false,
        captcha_reload: true,
      }));
    }
  };

  const checkInit = async () => {
    /**
     * X - Авторизация
     * ✓ - Ошибка navbar
     * X - 2fa config
     */
    if (navbar.error) {
      setData((prevState) => ({
        ...prevState,
        init: true,
        error: `Navbar: ${navbar.error}`,
      }));
      return;
    }

    /**
     * ✓ - Авторизация
     * X - Ошибка navbar
     * X - 2fa config
     */
    if (isAuth) {
      if (!menu || !menu.length) {
        try {
          const isEditor = isUrlEditor(query.redirect);
          if (isEditor) {
            window.location.href = '/admin/editor';
          }
          const navbar = await fetchNavbarConfig();
          setData((prevState) => ({
            ...prevState,
            redirectText: 'You are already authorized. Redirect...',
          }));
          await delay(500);
          const firstRoute = navbar?.menu[0];
          const findedRoute = getAccessRoutes()?.find((r) => r.link === query.redirect?.replace(/^(admin)|(\/admin)/gm, ''));
          history.push(findedRoute?.link || firstRoute?.url || '/users');
        } catch (error) {
          setData((prevState) => ({
            ...prevState,
            init: true,
          }));
        }
      }
    }

    if (isAuth && menu && menu?.length) {
      setData((prevState) => ({
        ...prevState,
        redirectText: 'You are already authorized. Redirect...',
      }));
      await delay(500);
      const firstRoute = menu[0];
      const findedRoute = getAccessRoutes()?.find((r) => r.link === query.redirect?.replace(/^(admin)|(\/admin)/gm, ''));
      const isEditor = isUrlEditor(query.redirect);
      isEditor ? (window.location.href = '/admin/editor') : history.push(findedRoute?.link || firstRoute?.url || '/users');
    }

    setData((prevState) => ({
      ...prevState,
      init: true,
    }));

    /**
     * Redirect to /admin
     */
    if (window?.location?.pathname === '/') {
      history.replace('/');
    }
  };

  useEffect(() => {
    if (captcha_reload) {
      setData((prevState) => ({
        ...prevState,
        captcha_reload: false,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captcha_reload]);

  useEffect(() => {
    document.title = '';
    checkInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!init) {
    return <Loader text={redirectText} />;
  }

  return (
    <div className="page-login">
      <Grid bsClass="container">
        <form className="page-login__form" ref={formRef} onSubmit={onSubmit}>
          {isLoading ? <Loader success={success} /> : null}
          {error || user.error ? <Alert bsStyle="danger">{error || user.error}</Alert> : null}
          <FormGroup>
            <ControlLabel htmlFor="login">Username</ControlLabel>
            <FormControl disabled={isLoading} type="text" id="login" name="login" required />
          </FormGroup>
          <FormGroup>
            <ControlLabel htmlFor="password">Password</ControlLabel>
            <FormControl disabled={isLoading} type="password" id="password" name="password" required />
          </FormGroup>
          {captcha && !captcha_reload ? (
            <FormGroup>
              <div className="page-login__form-captcha">
                {captcha_type === 'g-recaptcha' ? <ReCAPTCHA sitekey={captcha_key} ref={recaptcha} onErrored={onCaptchaError} /> : null}
                {captcha_type === 'h-captcha' ? <HCaptcha sitekey={captcha_key} ref={recaptcha} onError={onCaptchaError} /> : null}
              </div>
            </FormGroup>
          ) : null}
          <Button block type="submit" bsStyle="primary" disabled={isLoading}>
            <span>Sign in</span>
          </Button>
        </form>
      </Grid>
    </div>
  );
};

export default Login;
